import { CalendarClock, Clock, Film, PlaySquare } from "lucide-react";
import { CardShell, ScheduledBadge, parsePaths, type MetaFact } from "./CardShell";
import { isScheduled, scheduledDateLabel } from "../scheduled";
import { type Dict, type Lang } from "@/lib/i18n";
import { modulePoster } from "@/lib/lecturehub/poster";
import { moduleHref } from "../moduleHref";
import { formatDuration, formatEpisodes } from "@/lib/format";
import type { Module } from "@/lib/lecturehub/types";
import { isFreeToUnlock } from "../paywall";

export function VideoCard({
  lang,
  t,
  module: m,
  enrolled = false,
  cfHash = null,
  library = false,
}: {
  lang: Lang;
  t: Dict;
  module: Module;
  enrolled?: boolean;
  cfHash?: string | null;
  library?: boolean;
}) {
  const ott = m.ott;
  const duration = ott?.duration ?? null;
  const isSeries = ott?.videoType === "simple_series" || ott?.videoType === "section_series";
  const episodeCount = isSeries ? (ott?.videos?.length ?? 0) : null;
  const paths = parsePaths(m.keywords, lang);
  const href = moduleHref(lang, m, library);

  const scheduled = isScheduled(m);
  const scheduledLabel = scheduledDateLabel(t.detail, m.publishAt, lang);

  const facts: MetaFact[] = [];
  if (scheduledLabel) {
    facts.push({ Icon: CalendarClock, label: scheduledLabel });
  }
  if (episodeCount != null && episodeCount > 0) {
    facts.push({ Icon: Film, label: formatEpisodes(episodeCount, lang, t.units) });
  }
  if (duration && duration > 0) {
    facts.push({ Icon: Clock, label: formatDuration(duration) });
  }

  return (
    <CardShell
      t={t}
      href={href}
      openHref={href}
      poster={modulePoster(m, cfHash)}
      posterFallback={t.detail.typeVideo}
      typeLabel={t.detail.typeVideo}
      TypeIcon={PlaySquare}
      statusLabel={scheduled ? undefined : isFreeToUnlock(m.price) ? t.detail.free : undefined}
      posterBadge={scheduled ? <ScheduledBadge t={t} /> : undefined}
      title={m.name ?? "—"}
      abstract={m.abstract}
      enrolled={enrolled}
      paths={paths}
      facts={facts}
    />
  );
}
